import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { Stack } from "@mui/material";
import { toast } from "react-toastify";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
}));

const FlowBuilderInactivityModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [labels, setLabels] = useState({
    title: "Adicionar Inatividade",
    btn: "Adicionar"
  });
  const [textDig, setTextDig] = useState("");
  const [retryLimit, setRetryLimit] = useState("");
  const [intervals, setIntervals] = useState([]);

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar Inatividade",
        btn: "Salvar"
      });
      setTextDig(data?.data?.message || "");
      const rl = data?.data?.retryLimit ?? "";
      const ivRaw = Array.isArray(data?.data?.intervals)
        ? data.data.intervals.map(v => {
            if (typeof v === "object" && v !== null) {
              return { value: String(v.value ?? ""), unit: v.unit || "seconds" };
            }
            return { value: String(v), unit: "seconds" };
          })
        : [];
      setRetryLimit(String(rl));
      const count = parseInt(String(rl), 10) || 0;
      setIntervals(ivRaw.length ? ivRaw.slice(0, count) : Array.from({ length: count }, () => ({ value: "", unit: "seconds" })));
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: "Adicionar Inatividade",
        btn: "Adicionar"
      });
      setTextDig("");
      setRetryLimit("");
      setIntervals([]);
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open, data]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSave = async () => {
    if (!textDig || textDig.trim() === "") {
      toast.error("A mensagem de inatividade não pode estar em branco!");
      return;
    }
    const rl = parseInt(String(retryLimit), 10);
    if (isNaN(rl) || rl < 1) {
      toast.error("Defina um número válido para o máximo de avisos (mínimo 1).");
      return;
    }
    const normalizedIntervals = (intervals || []).slice(0, rl).map(v => {
      const n = parseInt(String(v?.value), 10);
      const unit = v?.unit || "seconds";
      return { value: isNaN(n) || n < 0 ? 1 : n, unit };
    });
    while (normalizedIntervals.length < rl) normalizedIntervals.push({ value: 1, unit: "seconds" });
    if (open === "edit") {
      handleClose();
      onUpdate({
        ...data,
        data: { message: textDig, retryLimit: rl, intervals: normalizedIntervals }
      });
      return;
    } else if (open === "create") {
      handleClose();
      onSave({ message: textDig, retryLimit: rl, intervals: normalizedIntervals });
    }
  };

  return (
    <div className={classes.root}>
      <Dialog open={activeModal} onClose={handleClose} fullWidth="md" scroll="paper">
        <DialogTitle id="form-dialog-title">{labels.title}</DialogTitle>
        <Stack>
          <DialogContent dividers>
            <Stack direction="row" spacing={2} style={{ marginTop: 12, width: "95%" }}>
              <TextField
                label={"Máximo de avisos"}
                variant="outlined"
                value={retryLimit}
                onChange={e => {
                  const val = e.target.value.replace(/[^0-9]/g, "");
                  setRetryLimit(val);
                  const rl = val === "" ? 0 : parseInt(val, 10);
                  setIntervals(prev => {
                    const next = [...prev];
                    if (next.length > rl) return next.slice(0, rl);
                    while (next.length < rl) next.push({ value: "", unit: "seconds" });
                    return next;
                  });
                }}
                className={classes.textField}
              />
            </Stack>
            <Stack direction="column" spacing={1} style={{ marginTop: 12, width: "95%" }}>
              {Array.from({ length: parseInt(String(retryLimit), 10) || 0 }).map((_, idx) => (
                <Stack key={`interval-${idx}`} direction="row" spacing={2}>
                  <TextField
                    label={`Intervalo entre avisos ${idx + 1}`}
                    variant="outlined"
                    value={intervals[idx]?.value || ""}
                    onChange={e => {
                      const v = e.target.value.replace(/[^0-9]/g, "");
                      setIntervals(prev => {
                        const next = [...prev];
                        next[idx] = { value: v, unit: next[idx]?.unit || "seconds" };
                        return next;
                      });
                    }}
                    margin="dense"
                    className={classes.textField}
                  />
                  <FormControl variant="outlined" margin="dense" style={{ minWidth: 160 }}>
                    <InputLabel>{`Unidade do aviso ${idx + 1}`}</InputLabel>
                    <Select
                      label={`Unidade do aviso ${idx + 1}`}
                      value={intervals[idx]?.unit || "seconds"}
                      onChange={e => {
                        const v = e.target.value;
                        setIntervals(prev => {
                          const next = [...prev];
                          next[idx] = { value: next[idx]?.value || "", unit: v };
                          return next;
                        });
                      }}
                    >
                      <MenuItem value="seconds">Segundos</MenuItem>
                      <MenuItem value="minutes">Minutos</MenuItem>
                      <MenuItem value="hours">Horas</MenuItem>
                      <MenuItem value="days">Dias</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              ))}
            </Stack>
            <TextField
              label={"Mensagem de inatividade"}
              multiline
              rows={6}
              name="text"
              variant="outlined"
              value={textDig}
              margin="dense"
              onChange={e => setTextDig(e.target.value)}
              className={classes.textField}
              style={{ width: "95%", marginTop: "10px" }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary" variant="outlined">
              Cancelar
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              className={classes.btnWrapper}
              onClick={handleSave}
            >
              {`${labels.btn}`}
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderInactivityModal;
